/**
 * Koodooka Motion Design Language
 *
 * Single source of truth for all animation timing across the admin portal.
 * Every component references these tokens — no magic numbers in component files.
 *
 * Aesthetic: macOS-inspired — content materialises in with subtle scale,
 * barely-perceptible spring bounce, and dissolves out crisply.
 * Banking staff use this 8 hours/day: fast enough to never block work,
 * smooth enough to feel premium.
 */

// ─── Duration tokens (seconds) ──────────────────────────────────────────────

export const duration = {
  instant: 0.1,     // Micro-feedback: button press, toggle
  fast: 0.2,        // State changes: collapse, expand, hover
  normal: 0.3,      // Content transitions: view switch, fade
  entrance: 0.7,    // Page entrance — unhurried, graceful
  exit: 0.35,       // Page exit — brisk but not abrupt
  dramatic: 0.6,    // Delete animation, major state change
} as const;

// ─── Easing tokens ──────────────────────────────────────────────────────────

export const ease = {
  // macOS-like deceleration curve for entrances
  out: [0.16, 1, 0.3, 1] as [number, number, number, number],
  // Fast ease-in for exits (content recedes away)
  in: [0.4, 0, 1, 1] as [number, number, number, number],
  // Symmetric ease for state changes
  inOut: [0.4, 0, 0.2, 1] as [number, number, number, number],
  // Snappy ease for micro-interactions
  snap: [0.22, 0.61, 0.36, 1] as [number, number, number, number],
} as const;

// ─── Spring configs ─────────────────────────────────────────────────────────

export const spring = {
  // macOS-inspired page spring — generous duration, gentle organic settle
  page: { type: 'spring' as const, duration: 0.7, bounce: 0.04 },
  // Snappy spring for sidebar indicator, button feedback
  snappy: { type: 'spring' as const, stiffness: 400, damping: 30 },
  // Soft spring for modal overlays
  soft: { type: 'spring' as const, stiffness: 200, damping: 25 },
} as const;

// ─── Page transition variants ───────────────────────────────────────────────
// macOS feel: content scales up from 94% + deblurs on entrance,
// recedes to 97% on exit. Asymmetric timing — enter is a gentle spring,
// exit is a fast tween so it never feels sluggish.
// transformOrigin MUST be 'center center' — this is what makes it elegant.

export const pageVariants = {
  initial: { opacity: 0, scale: 0.94, filter: 'blur(6px)' },
  animate: {
    opacity: 1, scale: 1, filter: 'blur(0px)',
    transition: { type: 'spring' as const, duration: 0.7, bounce: 0.04 },
  },
  exit: {
    opacity: 0, scale: 0.97, filter: 'blur(3px)',
    transition: { duration: 0.35, ease: [0.4, 0, 0.2, 1] as [number, number, number, number] },
  },
};

export const pageTransition = {
  ...spring.page,
} as const;

// Dashboard — same grace, slightly subtler (cards are the hero)
export const dashboardVariants = {
  initial: { opacity: 0, scale: 0.96 },
  animate: {
    opacity: 1, scale: 1,
    transition: { type: 'spring' as const, duration: 0.6, bounce: 0.02 },
  },
  exit: {
    opacity: 0, scale: 0.98,
    transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] as [number, number, number, number] },
  },
};

export const dashboardTransition = {
  type: 'spring' as const,
  duration: 0.6,
  bounce: 0.02,
} as const;

// Aliases used by other files (TheaterPage, InsuranceLayout, PageTransition)
export const fromLeftVariants = pageVariants;
export const fromRightVariants = pageVariants;
export const fadeVariants = dashboardVariants;
export type NavDirection = 'left' | 'right' | 'fade';

// ─── Card content transition (height/opacity only, NEVER x/y) ───────────────

export const cardContentTransition = {
  duration: duration.fast,
  ease: ease.inOut,
} as const;

// ─── CSS transition string for non-Framer elements ──────────────────────────

export const cssTransition = {
  fast: `${duration.instant}s cubic-bezier(${ease.snap.join(',')})`,
  normal: `${duration.fast}s cubic-bezier(${ease.inOut.join(',')})`,
  color: `${duration.normal}s cubic-bezier(${ease.inOut.join(',')})`,
} as const;
